import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FileText, Download, ExternalLink, Loader2, AlertCircle, ChevronUp } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

export default function PDFViewer({ pdfPath, title = "Document" }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [renderedPages, setRenderedPages] = useState(0);
  const containerRef = useRef(null);
  const pdfDocRef = useRef(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const renderAllPages = useCallback(async (pdf) => {
    const container = containerRef.current;
    if (!container) return;
    container.innerHTML = '';

    const containerWidth = container.clientWidth;

    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const viewport = page.getViewport({ scale: 1 });
      const scale = (containerWidth - 4) / viewport.width;
      const scaledViewport = page.getViewport({ scale });

      const canvas = document.createElement('canvas');
      canvas.width = scaledViewport.width * 2;
      canvas.height = scaledViewport.height * 2;
      canvas.style.width = `${scaledViewport.width}px`;
      canvas.style.height = `${scaledViewport.height}px`;
      canvas.style.display = 'block';
      if (i > 1) {
        canvas.style.marginTop = '8px';
        canvas.style.borderTop = '1px solid #e5e7eb';
        canvas.style.paddingTop = '8px';
      }

      const ctx = canvas.getContext('2d');
      ctx.scale(2, 2);

      await page.render({ canvasContext: ctx, viewport: scaledViewport }).promise;
      container.appendChild(canvas);
      setRenderedPages(i);
    }
  }, []);

  useEffect(() => {
    const baseUrl = window.location.origin;
    const fullUrl = `${baseUrl}${pdfPath.startsWith('/') ? pdfPath : '/' + pdfPath}`;

    const loadPdf = async () => {
      try {
        const pdf = await pdfjsLib.getDocument(fullUrl).promise;
        pdfDocRef.current = pdf;
        setPdfUrl(fullUrl);
        setNumPages(pdf.numPages);
        setLoading(false);
        await renderAllPages(pdf);
      } catch (err) {
        console.error('PDF load error:', err);
        setError(`Kon PDF niet laden: ${err.message}`);
        setLoading(false);
      }
    };

    loadPdf();

    return () => {
      if (pdfDocRef.current) {
        pdfDocRef.current.destroy();
      }
    };
  }, [pdfPath, renderAllPages]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDownload = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = title.replace(/\s+/g, '_') + '.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleOpenExternal = () => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">PDF laden...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-700 font-semibold mb-2">Fout bij laden PDF</p>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <FileText className="w-5 h-5 mr-2 text-blue-600" />
            {title}
          </h3>
          {numPages > 0 && (
            <p className="text-sm text-gray-500 mt-1">{numPages} pagina{numPages !== 1 ? "'s" : ''}</p>
          )}
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleDownload}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Download</span>
          </button>
          <button
            onClick={handleOpenExternal}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            <span className="hidden sm:inline">Nieuw tabblad</span>
          </button>
        </div>
      </div>

      {renderedPages > 0 && renderedPages < numPages && (
        <div className="mb-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-700">
          <Loader2 className="w-4 h-4 animate-spin" />
          Pagina {renderedPages} van {numPages} geladen...
        </div>
      )}

      <div
        ref={containerRef}
        className="w-full rounded-lg border-2 border-gray-200 bg-white p-2 overflow-hidden"
      />

      {showScrollTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full bg-blue-600 text-white shadow-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
        >
          <ChevronUp className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}
